import { useCallback, useEffect, useMemo, useState } from 'react'
import { TokenData } from '@opendollar/sdk/lib/contracts/addreses'
import { ChevronLeft, Info, Loader } from 'react-feather'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import useGeb from '~/hooks/useGeb'
import { Tooltip as ReactTooltip } from 'react-tooltip'
import styled from 'styled-components'
import { ethers } from 'ethers'
import { useTransactionAdder } from '~/hooks'
import { DEFAULT_SAFE_STATE, getTokenLogo, formatNumber, formatWithCommas } from '~/utils'
import { useStoreActions, useStoreState } from '~/store'
import TokenInput from '~/components/TokenInput'
import Modal from '~/components/Modals/Modal'
import Dropdown from '~/components/Dropdown'
import Button from '~/components/Button'
import Review from './Review'
import {
    handleTransactionError,
    useActiveWeb3React,
    useInputsHandlers,
    useTokenBalanceInUSD,
    useTokenApproval,
    ApprovalState,
    useSafeInfo,
    StatsType,
} from '~/hooks'
import ConnectWalletStep from '~/components/ConnectWalletStep'
import { useCollateralBalances } from '~/hooks/useCollateralBalances'
import BridgeModal from '~/components/Modals/BridgeModal'

const CreateVault = ({
    selectedItem,
    setSelectedItem,
    collaterals,
}: {
    selectedItem: string
    setSelectedItem: (item: string) => void
    collaterals: TokenData[]
}) => {
    const geb = useGeb()
    const addTransaction = useTransactionAdder()
    const { stats, error, availableHai, parsedAmounts, totalCollateral, totalDebt, collateralRatio, liquidationPrice } =
        useSafeInfo('create')
    const { connectWalletModel: connectWalletState } = useStoreState((state) => state)
    const { blockNumber } = connectWalletState
    const { provider, account, chainId } = useActiveWeb3React()
    const [showPreview, setShowPreview] = useState(false)
    const {
        safeModel: safeState,
        connectWalletModel: { proxyAddress, tokensData, tokensFetchedData },
    } = useStoreState((state) => state)
    const navigate = useNavigate()
    const {
        safeModel: safeActions,
        connectWalletModel: connectWalletActions,
        popupsModel: popupsActions,
    } = useStoreActions((state) => state)
    const { leftInput, rightInput } = parsedAmounts
    const { onLeftInput, onRightInput, onClearAll: clearAll } = useInputsHandlers()
    const { t } = useTranslation()
    const isValid = !error
    // const [needsBridge, setNeedsBridge] = useState(false)
    const formattedCollateralBalances = useMemo(() => {
        return collaterals.reduce((acc, collateral) => {
            const balance = tokensFetchedData[collateral.symbol]?.balanceE18 || '0'
            const formattedBalance = ethers.utils.formatEther(balance)
            return { ...acc, [collateral.symbol]: formattedBalance }
        }, {} as { [symbol: string]: string })
    }, [collaterals, tokensFetchedData])

    const collateralsDropdown = collaterals.map((collateral) => {
        return {
            name: collateral.symbol,
            icon: getTokenLogo(collateral.symbol),
            value: formatWithCommas(formattedCollateralBalances[collateral.symbol]),
        }
    })

    const dropdownSelected = collateralsDropdown.find((item) => item.name === selectedItem)!

    const selectedCollateral = tokensData && tokensData[selectedItem]
    const selectedCollateralBalance = formattedCollateralBalances[selectedItem]
    const selectedCollateralDecimals = tokensFetchedData[selectedItem].decimals

    const redirectToWrapEth = () => {
        const url = 'https://wrapeth.com/'
        window.open(url, '_blank')
    }

    const selectedTokenBalance = useCollateralBalances(tokensData, tokensFetchedData, selectedItem)

    const collateralUnitPriceUSD = formatNumber(
        safeState.liquidationData?.collateralLiquidationData[selectedCollateral.symbol]?.currentPrice?.value || '0'
    )

    const selectedTokenBalanceInUSD = formatNumber(
        (Number(collateralUnitPriceUSD) * Number(selectedCollateralBalance)).toString()
    )

    const debtFloor = Math.ceil(
        Number(
            formatNumber(
                safeState.liquidationData?.collateralLiquidationData[selectedCollateral.symbol]?.debtFloor || '0'
            )
        )
    )
    const haiBalanceUSD = useTokenBalanceInUSD('OD', rightInput ? rightInput : availableHai)

    //const maxHaiWithBuffer = Math.trunc(+availableHai - (+availableHai * 5) / 100)
    const onMaxLeftInput = () => onLeftInput(selectedTokenBalance.toString())
    //available hai - 5% of available hai, this is a buffer to prevent bugs.
    const onMaxRightInput = () => onRightInput(availableHai.toString())

    const onClearAll = useCallback(() => {
        clearAll()
    }, [clearAll])

    const handleWaitingTitle = () => {
        return 'Creating Vault'
    }

    const handleSubmit = () => {
        safeActions.setSafeData({
            leftInput: parsedAmounts.leftInput ? parsedAmounts.leftInput : '0',
            rightInput: parsedAmounts.rightInput ? parsedAmounts.rightInput : '0',
            totalCollateral,
            totalDebt,
            collateralRatio: collateralRatio as number,
            liquidationPrice: liquidationPrice as number,
            collateral: selectedItem,
        })

        setShowPreview(true)
    }

    const reset = () => {
        onClearAll()
        safeActions.setSafeData({
            ...DEFAULT_SAFE_STATE,
            collateral: selectedItem,
        })
        connectWalletActions.setIsStepLoading(true)
        safeActions.setIsSafeCreated(true)
    }

    const wrapEth = () => {
        redirectToWrapEth()
    }

    const handleConfirm = async () => {
        if (account && provider) {
            safeActions.setIsSuccessfulTx(false)
            setShowPreview(false)
            popupsActions.setIsWaitingModalOpen(true)
            popupsActions.setWaitingPayload({
                title: 'Waiting For Confirmation',
                text: handleWaitingTitle(),
                hint: 'Confirm this transaction in your wallet',
                status: 'loading',
            })
            safeActions.setSafeData({
                ...safeState.safeData,
                leftInput: parsedAmounts.leftInput || '0',
                rightInput: parsedAmounts.rightInput || '0',
                totalCollateral,
                totalDebt,
            })
            const signer = provider.getSigner(account)
            try {
                connectWalletActions.setIsStepLoading(true)
                const depositAmountUSD = collateralUnitPriceUSD ? Number(collateralUnitPriceUSD) : 0
                const borrowAmountUSD = haiBalanceUSD ? Number(haiBalanceUSD) : 0
                await safeActions.depositAndBorrow({
                    safeData: safeState.safeData,
                    signer,
                    geb,
                    account,
                    depositAmountUSD,
                    borrowAmountUSD,
                })
                navigate('/vaults')
                safeActions.setIsSuccessfulTx(true)
                popupsActions.setIsWaitingModalOpen(false)
            } catch (e) {
                safeActions.setIsSuccessfulTx(false)
                handleTransactionError(e)
            } finally {
                reset()
            }
        }
    }

    let [approvalState, approve] = useTokenApproval(
        leftInput,
        selectedCollateral?.address,
        proxyAddress,
        selectedCollateralDecimals,
        true
    )

    const handleCreateAccount = async () => {
        if (!account || !provider || !chainId) return false
        const txData = await geb.contracts.proxyRegistry.populateTransaction['build()']()
        const signer = provider.getSigner(account)
        try {
            connectWalletActions.setIsStepLoading(true)
            popupsActions.setWaitingPayload({
                title: 'Waiting For Confirmation',
                text: `Creating facilitator`,
                hint: 'Confirm this transaction in your wallet',
                status: 'loading',
            })
            const txResponse = await signer.sendTransaction(txData)
            connectWalletActions.setCtHash(txResponse.hash)
            addTransaction({ ...txResponse, blockNumber: blockNumber[chainId] }, 'Creating an account')
            popupsActions.setWaitingPayload({
                title: 'Transaction Submitted',
                hash: txResponse.hash,
                status: 'success',
            })
            await txResponse.wait()
        } catch (e) {
            connectWalletActions.setStep(2)
            connectWalletActions.setIsStepLoading(false)
            handleTransactionError(e)
        }
        connectWalletActions.setIsStepLoading(false)
    }

    // useEffect(() => {
    //     if (!account || !provider || !selectedCollateral) return
    //     const checkNeedsBridge = async () => {
    //         setNeedsBridge(
    //             await checkUserHasBalance(selectedCollateral.address, account, provider, parsedAmounts.leftInput)
    //         )
    //     }
    //     checkNeedsBridge()
    // }, [account, provider, selectedCollateral, parsedAmounts.leftInput])

    // const setBridge = (reason: string) => {
    //     bridgeModelActions.setReason(reason)
    //     bridgeModelActions.setFromTokenSymbol(selectedCollateral?.symbol)
    //     bridgeModelActions.setToTokenAddress(selectedCollateral?.address)
    //     navigate('/bridge')
    // }

    return (
        <>
            <Modal
                isModalOpen={showPreview}
                closeModal={() => setShowPreview(false)}
                maxWidth={'450px'}
                backDropClose
                hideHeader
                hideFooter
                handleModalContent
            >
                <ReviewContainer>
                    <Review type={'create'} />
                    <BtnContainer>
                        <Button id="create_confirm" onClick={handleConfirm}>
                            {'Confirm Transaction'}
                        </Button>{' '}
                    </BtnContainer>
                </ReviewContainer>
            </Modal>
            <BridgeModal />
            <InnerContent>
                <Content>
                    <Header>
                        <Btn>
                            <FlexBtn onClick={() => navigate(-1)}>
                                <ChevronLeft />
                                <span>Back</span>
                            </FlexBtn>
                        </Btn>
                        <span className="title"> {t('create_safe')}</span>
                    </Header>
                    <Box>
                        <ColWrapper>
                            {selectedCollateral ? (
                                <Col>
                                    <DropDownContainer>
                                        <SideLabel>{`Select Collateral Type`}</SideLabel>
                                        <Dropdown
                                            items={collateralsDropdown}
                                            itemSelected={dropdownSelected}
                                            getSelectedItem={setSelectedItem}
                                            fontSize="14px"
                                        />
                                        {dropdownSelected.name === 'WETH' && (
                                            <WrapBox>
                                                Don't have WETH?{' '}
                                                <WrapBtn onClick={wrapEth} color="secondary">
                                                    Wrap ETH
                                                </WrapBtn>
                                            </WrapBox>
                                        )}
                                        {/* {needsBridge && !parsedAmounts.leftInput && (
                                            <BridgeLabel>
                                                {`Insufficient funds. Move assets to Arbitrum using the `}
                                                <BridgeButton
                                                    onClick={() => {
                                                        setBridge(
                                                            `Insufficient Collateral Balance: ${selectedCollateral.symbol}`
                                                        )
                                                    }}
                                                >
                                                    Bridge
                                                </BridgeButton>
                                            </BridgeLabel>
                                        )} */}
                                    </DropDownContainer>

                                    <Inputs>
                                        <SideLabel>{`Deposit ${selectedItem} and Borrow OD`}</SideLabel>

                                        <TokenInput
                                            token={
                                                selectedCollateral?.symbol
                                                    ? {
                                                          name: selectedCollateral?.symbol || '-',
                                                          icon: getTokenLogo(selectedCollateral?.symbol),
                                                      }
                                                    : undefined
                                            }
                                            label={`Balance: ${formatWithCommas(selectedTokenBalance, 4)} ${
                                                selectedCollateral?.symbol
                                            }`}
                                            rightLabel={`~$${formatWithCommas(selectedTokenBalanceInUSD, 2)}`}
                                            onChange={onLeftInput}
                                            value={leftInput}
                                            handleMaxClick={onMaxLeftInput}
                                            data_test_id="deposit_borrow"
                                            decimals={Number(selectedCollateralDecimals)}
                                        />
                                        {/* {needsBridge && parsedAmounts.leftInput && (
                                            <BridgeLabel>
                                                {`Insufficient funds. Move assets to Arbitrum using the `}
                                                <BridgeButton
                                                    onClick={() => {
                                                        setBridge(
                                                            `Insufficient Collateral Balance: ${selectedCollateral.symbol}`
                                                        )
                                                    }}
                                                >
                                                    Bridge
                                                </BridgeButton>
                                            </BridgeLabel>
                                        )} */}
                                        <br />
                                        <TokenInput
                                            token={
                                                tokensData.OD && {
                                                    icon: getTokenLogo(tokensData.OD.symbol),
                                                    name: tokensData.OD.symbol,
                                                }
                                            }
                                            label={`Borrow OD: ${formatWithCommas(availableHai, 4)} ${
                                                tokensData.OD?.symbol
                                            }`}
                                            rightLabel={`~$${formatWithCommas(haiBalanceUSD)}`}
                                            onChange={onRightInput}
                                            value={rightInput}
                                            handleMaxClick={onMaxRightInput}
                                            data_test_id="repay_withdraw"
                                        />
                                    </Inputs>
                                    <Note data-test-id="debt_floor_note">
                                        {`The minimum deposit size is ~$${
                                            debtFloor *
                                            Number(
                                                safeState.liquidationData!.collateralLiquidationData[
                                                    selectedCollateral?.symbol
                                                ].liquidationCRatio
                                            )
                                        }.`}
                                        <br />
                                        {`The minimum amount to mint per vault is ${debtFloor} OD`}
                                    </Note>
                                </Col>
                            ) : (
                                <Col>
                                    <Loader width={'100%'} />
                                </Col>
                            )}

                            <Col>
                                <Stats>
                                    <SubTitle>Overview</SubTitle>
                                    {Object.keys(stats).map((key) => {
                                        const isPrimary = key === 'data'
                                        return (
                                            <div key={key} className="blockie">
                                                {stats[key as StatsType].map((item) => {
                                                    return (
                                                        <StatItemWrapper key={item.label}>
                                                            <Flex>
                                                                <Label color={isPrimary ? 'primary' : 'secondary'}>
                                                                    {item.label}
                                                                    {item.tip ? (
                                                                        <InfoIcon
                                                                            data-tooltip-id="tooltip-create-vault"
                                                                            data-tooltip-content={item.tip}
                                                                        >
                                                                            <Info size="13" color="#1C293A" />
                                                                        </InfoIcon>
                                                                    ) : null}
                                                                </Label>
                                                                <Value>
                                                                    {item.value !== '-' &&
                                                                    item.label.toLowerCase().includes('collateral') &&
                                                                    item.label.toLowerCase().includes('total')
                                                                        ? formatWithCommas(item.value)
                                                                        : item.value}
                                                                </Value>
                                                            </Flex>
                                                        </StatItemWrapper>
                                                    )
                                                })}
                                            </div>
                                        )
                                    })}
                                </Stats>
                            </Col>
                        </ColWrapper>
                        <FooterWrapper>
                            <Btn className="clear" onClick={reset}>
                                Clear All
                            </Btn>
                            <Flex className="hasBtn">
                                {error === 'Create Account' && (
                                    <Button onClick={handleCreateAccount}>{error ?? 'Create Account'}</Button>
                                )}
                                {approvalState === ApprovalState.APPROVED ? (
                                    <Button onClick={handleSubmit} disabled={!isValid}>
                                        {error ?? 'Review Transaction'}
                                    </Button>
                                ) : approvalState === ApprovalState.PENDING ? (
                                    <Button disabled={true}>Pending Approval..</Button>
                                ) : (
                                    error !== 'Create Account' && (
                                        <Button onClick={approve} disabled={!isValid}>
                                            {error ?? `Approve ${selectedItem}`}
                                        </Button>
                                    )
                                )}
                            </Flex>
                        </FooterWrapper>
                    </Box>

                    <ReactTooltip
                        style={{ zIndex: 99, maxWidth: '400px' }}
                        id="tooltip-create-vault"
                        variant="light"
                        data-effect="solid"
                    />
                </Content>
            </InnerContent>
        </>
    )
}

const CreateVaultContainer = () => {
    const {
        safeModel: {
            liquidationData,
            safeData: { collateral },
        },
        connectWalletModel: { tokensData, tokensFetchedData },
    } = useStoreState((state) => state)
    const collaterals = tokensData ? Object.values(tokensData).filter((token) => token.isCollateral) : []
    const [selectedItem, setSelectedItem] = useState<string>('')

    const { safeModel: safeActions } = useStoreActions((state) => state)

    useEffect(() => {
        safeActions.setSafeData({ ...DEFAULT_SAFE_STATE, collateral: selectedItem })
        return () => safeActions.setSafeData(DEFAULT_SAFE_STATE)

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedItem])

    useEffect(() => {
        if (collaterals.length > 0 && selectedItem === '') setSelectedItem(collaterals[0].symbol)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [collaterals])

    return (
        <Container>
            {liquidationData && tokensData && collateral && collateral !== '' && tokensFetchedData[selectedItem] ? (
                <CreateVault selectedItem={selectedItem} setSelectedItem={setSelectedItem} collaterals={collaterals} />
            ) : (
                <ConnectWalletStep />
            )}
        </Container>
    )
}

export default CreateVaultContainer

const ReviewContainer = styled.div`
    padding: 20px;
    border-radius: 4px;
    background: linear-gradient(to bottom, #1a74ec, #6396ff);
`
const Container = styled.div`
    max-width: 1362px;
    margin: 80px auto;
    padding: 0 15px;
    @media (max-width: 767px) {
        margin: 50px auto;
    }
`
const InnerContent = styled.div`
    border-radius: 20px;
    background: ${(props) => props.theme.colors.colorSecondary};
    font-family: 'Open Sans', sans-serif;
`

const Content = styled.div`
    padding: 20px;
`

const StatItemWrapper = styled.div`
    border-bottom: 1px solid #1a74ec4d;

    &:last-child {
        border: 0;
    }
`

const BtnContainer = styled.div`
    margin-top: 24px;
    text-align: center;
    border: 2px solid #e2f1ff;
    width: 100%;
    border-radius: 4px;
    font-family: 'Barlow', sans-serif;
    font-size: 18px;
    font-weight: 600;
    line-height: 22px;
`
const Header = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 55px;

    span {
        flex: 0 0 55px;
        font-size: 14px;

        &.title {
            display: block;
            flex: 1;
            font-family: 'Barlow', sans-serif;
            text-align: center;
            font-weight: bold;
            font-size: ${(props) => props.theme.font.xxLarge};
            color: #1c293a;
        }
    }
`
const Btn = styled.button`
    border: 0;
    padding: 0;
    border-radius: 0;
    box-shadow: none;
    outline: none;
    background: transparent;
`

const FlexBtn = styled.div`
    display: flex;
    align-items: center;

    span {
        color: ${(props) => props.theme.colors.accent};
        text-transform: uppercase;
        font-weight: 700;
    }

    svg {
        color: ${(props) => props.theme.colors.accent};
        cursor: pointer;
        width: 50px;
    }
`

const WrapBtn = styled(Btn)`
    color: ${(props) => props.theme.colors.accent};
`

const WrapBox = styled.div`
    margin-top: 12px;
    font-size: 14px;
`

const Box = styled.div`
    display: flex;
    flex-direction: column;
    box-shadow: 0px 4px 6px 0px #0d4b9d33;

    padding: 22px;
    border-radius: 8px;
    background: white;
`

const FooterWrapper = styled.div`
    display: flex;
    justify-content: space-between;

    .clear {
        cursor: pointer;
        color: ${(props) => props.theme.colors.accent};
        font-size: 14px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 2px;
    }

    @media (max-width: 767px) {
        flex-direction: column;
        padding: 15px 0;
    }
`

const ColWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    margin-bottom: 30px;

    @media (max-width: 767px) {
        flex-direction: column;
        padding: 15px 0;
    }
`

const Col = styled.div`
    flex: 0 0 48%;
    z-index: 1;
`

const DropDownContainer = styled.div``

export const SideLabel = styled.div`
    font-weight: 400;
    font-size: ${(props) => props.theme.font.default};
    color: ${(props) => props.theme.colors.tertiary};
    margin-bottom: 10px;
`

const Inputs = styled.div`
    margin-top: 30px;
`

const Stats = styled.div`
    background: ${(props) => props.theme.colors.background};
    padding: 24px;
    border-radius: 4px;
    .blockie {
        border-bottom: 1px solid #1a74ec4d;
        &:last-child {
            border: 0;
        }
    }
    @media (max-width: 767px) {
        margin-top: 20px;
    }
`
const SubTitle = styled.div`
    color: ${(props) => props.theme.colors.accent};
    font-size: 22px;
    font-weight: 700;
    font-family: 'Barlow', sans-serif;
`

const Flex = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 13px 0;
    &.hasBtn {
        margin: 0 0 20px 0;
        button {
            width: 100%;
            text-align: center;
        }
        @media (max-width: 767px) {
            flex-direction: column;
            button {
                margin-top: 20px;
            }
        }
    }
`
const Label = styled.div<{ color?: 'primary' | 'secondary' }>`
    font-size: ${(props) => props.theme.font.default};
    font-weight: 400;
    color: ${(props) => props.theme.colors.accent};
    display: flex;
    align-items: center;
`

const Value = styled.div`
    font-size: ${(props) => props.theme.font.small};
    font-weight: 700;
    color: ${(props) => props.theme.colors.accent};
`

const InfoIcon = styled.div`
    cursor: pointer;
    svg {
        /* fill: ${(props) => props.theme.colors.secondary}; */
        color: ${(props) => props.theme.colors.placeholder};
        position: relative;
        width: 20px;
        height: 20px;
        margin-left: 8px;
    }
`

const Note = styled.div`
    color: ${(props) => props.theme.colors.accent};
    background-color: ${(props) => props.theme.colors.background};
    padding-left: 18px;
    padding-top: 15px;
    padding-bottom: 15px;
    margin-top: 20px;
    border-radius: 4px;
    border-left: 3px solid ${(props) => props.theme.colors.primary};
    font-size: 15px;
    font-weight: 600;
    color: ${(props) => props.theme.colors.primary};
`

// const BridgeLabel = styled.div`
//     color: #e39806;
//     font-size: 14px;
//     margin-top: 10px;
// `

// const BridgeButton = styled.span`
//     color: ${(props) => props.theme.colors.primary};
//     cursor: pointer;
// `
